import { useEffect, useRef, useState } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import SectionHeading from '../../components/base/SectionHeading';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { attachTilt } from '../../hooks/useTiltCards';
import ContactForm from './components/ContactForm';

function ServicesSection() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.1 });
  const tiltRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 4);
  }, []);

  const services = [
    {
      i: 'ri-compass-3-line',
      t: 'Strategic guidance',
      d: 'Quarterly working sessions with your operating partner. Roadmaps, hard decisions, and second opinions on demand.',
    },
    {
      i: 'ri-team-line',
      t: 'Hiring & talent',
      d: 'Senior intros and active recruiting support. Engineers, designers, GMs — we’ve built our own bench.',
    },
    {
      i: 'ri-bar-chart-2-line',
      t: 'Fundraising prep',
      d: 'Narrative work, deck sharpening, financial modeling. We sit through your dry runs and pressure-test the story.',
    },
    {
      i: 'ri-shield-check-line',
      t: 'Diligence support',
      d: 'Technical and market diligence on partners and acquisitions. We bring sector specialists into the room.',
    },
    {
      i: 'ri-megaphone-line',
      t: 'GTM & launch',
      d: 'Positioning, PR, and design support for major company moments — from stealth lift to product launch.',
    },
    {
      i: 'ri-government-line',
      t: 'Board & governance',
      d: 'Board seats or independent observer roles. Calm in crisis, sharp in strategy.',
    },
  ];

  return (
    <section ref={revealRef} className="relative bg-[#070709] py-24 md:py-32 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="Services"
          title="Six ways we get hands-on."
          description="Advisory is not a side product — it’s how every founder we back experiences Vex on a weekly basis."
          className="mb-16"
        />
        <div ref={tiltRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div
              key={s.t}
              className="gsap-reveal tilt-card hover-glow glass-card rounded-2xl p-7 min-h-[240px] flex flex-col gap-5"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl liquid-glass tilt-lift">
                <i className={`${s.i} text-xl`}></i>
              </div>
              <div>
                <h3 className="font-display text-xl md:text-2xl mb-2">
                  <span className="title-shimmer inline-block">{s.t}</span>
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const ref = useScrollReveal<HTMLDivElement>({ stagger: 0.08 });
  const faqs = [
    {
      q: 'Do you only back companies you’ve invested in?',
      a: 'Mostly. Advisory is bundled into every check we write. In rare cases we take on independent advisory work — usually for funds or strategic acquirers.',
    },
    {
      q: 'What stage do you typically work with?',
      a: 'Pre-seed and seed are our sweet spot. We support through Series A and step back when later-stage funds take the lead.',
    },
    {
      q: 'Can you lead a round?',
      a: 'Yes. We lead roughly 40% of the deals we participate in and are happy to set terms, organize syndicates, and run process.',
    },
    {
      q: 'What does “first call” actually look like?',
      a: 'A 30-minute video call with one of our partners. No deck required. We listen, ask sharp questions, and follow up within 72 hours.',
    },
    {
      q: 'Do you sign NDAs?',
      a: 'No — like most early-stage firms, we don’t sign NDAs at first contact. We treat everything you share as confidential by default.',
    },
  ];
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section ref={ref} className="relative bg-[#050505] py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16">
        <SectionHeading
          eyebrow="FAQ"
          title="Questions we hear often."
          className="mb-14"
        />
        <div className="border-t border-white/10">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="gsap-reveal border-b border-white/10">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full text-left py-6 flex items-center justify-between gap-6 group cursor-pointer"
                >
                  <span className="font-display text-lg md:text-xl text-white group-hover:text-white">
                    {f.q}
                  </span>
                  <span className={`w-9 h-9 flex items-center justify-center rounded-full border border-white/15 shrink-0 transition-transform ${isOpen ? 'rotate-45' : ''}`}>
                    <i className="ri-add-line"></i>
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ${
                    isOpen ? 'max-h-40 pb-6' : 'max-h-0'
                  }`}
                >
                  <p className="text-white/65 text-sm md:text-base leading-relaxed max-w-2xl">
                    {f.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const ref = useScrollReveal<HTMLDivElement>({ stagger: 0.1 });
  return (
    <section ref={ref} className="relative bg-[#070709] py-24 md:py-32 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 gsap-reveal">
            <SectionHeading
              eyebrow="Start a chat"
              title="Tell us what you’re building."
              description="Founders, operators, and friends of the firm. Drop us a line and we’ll respond within 72 hours."
            />
            <div className="mt-10 space-y-4 text-sm">
              <a href="mailto:hello@vex.vc" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                <i className="ri-mail-line text-lg"></i> hello@vex.vc
              </a>
              <div className="flex items-center gap-3 text-white/70">
                <i className="ri-map-pin-line text-lg"></i> SF · NYC · London · Singapore
              </div>
              <div className="flex items-center gap-3 text-white/70">
                <i className="ri-time-line text-lg"></i> Office hours: every Thursday
              </div>
            </div>
          </div>
          <div className="lg:col-span-3 gsap-reveal">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AdvisoryPage() {
  return (
    <PageLayout transparentNav>
      <PageHero
        eyebrow="Advisory"
        title="Operators on call — when it matters most."
        description="Beyond capital, we offer hands-on advisory: strategy, hiring, fundraising, and the hard 2am conversations. Every Vex partner is reachable, and we mean it."
        image="https://readdy.ai/api/search-image?query=Cinematic%20minimalist%20still%20life%20of%20a%20vintage%20brass%20compass%20resting%20on%20a%20dark%20linen%20surface%20with%20soft%20warm%20amber%20directional%20studio%20light%2C%20editorial%20composition%20with%20deep%20negative%20space%2C%20premium%20editorial%20technology%20aesthetic&width=1800&height=900&seq=vex-advisory-hero&orientation=landscape"
      />
      <ServicesSection />
      <FaqSection />
      <ContactSection />
    </PageLayout>
  );
}